(() => {
  const status = document.getElementById('CrgSearchDemoStatus');
  if (!status) return;
  const initial = new URL(location.href);
  const strings = JSON.parse(document.getElementById('CrgSearchDemoStrings').textContent);
  const expectedCount = Number(status.dataset.count);
  const page = Number(initial.searchParams.get('page') || 1);
  const pageSize = 24;
  const storageKey = 'crg-search-preview-snapshot-v2';
  const parameters = new URLSearchParams(initial.search);
  ['page', 'preview_theme_id', 'crg_revision'].forEach((name) => parameters.delete(name));
  parameters.sort();
  const queryKey = [initial.pathname, status.dataset.context, parameters.toString()].join('|');
  const nativeCards = new Map();
  const grid = document.querySelector('#product-grid > ul');
  let nativeObserver;
  const say = (name, values = {}) => {
    status.textContent = strings[name].replace(/\{(\w+)\}/g, (_, field) => values[field] ?? '');
  };
  const fallback = (error) => {
    nativeObserver?.disconnect();
    delete status.dataset.verified;
    delete status.dataset.page;
    if (error) {
      status.dataset.failure = error.message;
      try {
        const stored = JSON.parse(sessionStorage.getItem(storageKey));
        if (stored?.key === queryKey) sessionStorage.removeItem(storageKey);
      } catch {}
    }
    say('fallback');
  };
  if (!grid || !Number.isInteger(page) || page < 1 || !Number.isInteger(expectedCount) || expectedCount < 1 || expectedCount > 400 || page > Math.ceil(expectedCount / pageSize) || (initial.searchParams.get('sort_by') && initial.searchParams.get('sort_by') !== 'relevance') || initial.searchParams.has('type') || [...initial.searchParams.keys()].some((name) => name.startsWith('filter.'))) {
    fallback();
    return;
  }
  const container = document.getElementById('ProductGridContainer');
  if (container) {
    nativeObserver = new MutationObserver(() => {
      if (document.querySelector('#product-grid > ul') !== grid) {
        fallback();
        nativeObserver.disconnect();
      }
    });
    nativeObserver.observe(container, { childList: true });
  }
  [...grid.children].forEach((card) => {
    const id = card.querySelector('a[id^="CardLink-"]')?.id.split('-').pop();
    if (id) nativeCards.set(id, card);
  });
  const initialIds = [...nativeCards.keys()];
  const validRows = (rows) => {
    if (!Array.isArray(rows) || rows.length !== expectedCount || new Set(rows.map((row) => row[0])).size !== expectedCount) return false;
    let soldOut = false;
    for (const row of rows) {
      if (!Array.isArray(row) || row.length !== 5 || !Number.isSafeInteger(row[0]) || row[3] !== 'product' || typeof row[2] !== 'boolean' || !/^[a-z0-9-]+$/.test(row[1])) return false;
      const productURL = new URL(row[4], initial.origin);
      if (productURL.origin !== initial.origin || productURL.pathname !== `/products/${row[1]}`) return false;
      if (!row[2]) soldOut = true;
      else if (soldOut) return false;
    }
    return true;
  };
  const request = async (url) => {
    const response = await fetch(url.toString(), { signal: AbortSignal.timeout(15000), credentials: 'same-origin' });
    if (!response.ok) throw new Error('Request failed');
    return response;
  };
  const windowRequest = async (size, number) => {
    const url = new URL(initial);
    url.searchParams.set('view', size === 200 ? 'crg-search-index-200' : 'crg-search-index');
    url.searchParams.set('page', number);
    return (await request(url)).json();
  };
  const loadSnapshot = async () => {
    const savedSnapshot = sessionStorage.getItem(storageKey);
    try {
      const stored = JSON.parse(savedSnapshot);
      if (stored?.key === queryKey && stored.expires > Date.now() && validRows(stored.rows)) {
        status.dataset.snapshotSource = 'session';
        return stored.rows;
      }
    } catch {}
    const windows = await Promise.all(expectedCount > 250 ? [windowRequest(250, 1), windowRequest(200, 2)] : [windowRequest(250, 1)]);
    const unique = new Map();
    windows.forEach((window, index) => {
      const size = index === 0 ? 250 : 200;
      if (window.count !== expectedCount || window.page !== index + 1 || window.page_size !== size || !Array.isArray(window.items) || window.items.length !== Math.min(size, expectedCount - index * size) || new Set(window.items.map((row) => row[0])).size !== window.items.length) throw new Error('Invalid window');
      window.items.forEach((row) => {
        const previous = unique.get(row[0]);
        if (previous && previous.slice(0, 4).some((value, field) => value !== row[field])) throw new Error('Changed overlapping result');
        if (!previous) unique.set(row[0], row);
      });
    });
    const rows = [...unique.values()];
    if (!validRows(rows)) throw new Error('Incomplete or changed results');
    const saved = JSON.stringify({ key: queryKey, expires: Date.now() + 300000, rows });
    sessionStorage.setItem(storageKey, saved);
    if (sessionStorage.getItem(storageKey) !== saved) throw new Error('Session storage unavailable');
    status.dataset.snapshotSource = 'fresh';
    return rows;
  };
  const loadMissingCards = async (rows) => {
    const missing = rows.filter((row) => !nativeCards.has(String(row[0])));
    status.dataset.missingCards = missing.length;
    const batches = [];
    for (let offset = 0; offset < missing.length; offset += 12) batches.push(missing.slice(offset, offset + 12));
    await Promise.all(batches.map(async (batch) => {
      const url = new URL(initial.pathname, initial.origin);
      url.searchParams.set('view', 'crg-search-card-batch');
      url.searchParams.set('q', batch.map((row) => row[1]).join(','));
      const response = await request(url);
      const raw = await response.text();
      if (raw.length > 200000) throw new Error('Oversize card response');
      const holder = document.createElement('template');
      holder.innerHTML = raw;
      const root = holder.content.querySelector('[data-crg-card-batch]');
      const cards = root ? [...root.querySelectorAll('[data-crg-result]')] : [];
      if (!root || Number(root.dataset.requested) !== batch.length || cards.length !== batch.length) throw new Error('Incomplete cards');
      batch.forEach((row, index) => {
        const card = cards[index];
        if (card.dataset.id !== String(row[0]) || card.dataset.handle !== row[1] || card.dataset.available !== String(row[2])) throw new Error('Changed product');
        card.querySelectorAll('a[href]').forEach((anchor) => {
          if (new URL(anchor.href, initial.origin).pathname === `/products/${row[1]}`) anchor.setAttribute('href', row[4]);
        });
        const item = document.createElement('li');
        item.className = 'grid__item';
        item.append(...card.childNodes);
        nativeCards.set(String(row[0]), item);
      });
    }));
  };
  const run = async () => {
    const rows = await loadSnapshot();
    const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);
    for (const row of pageRows) {
      const existing = nativeCards.get(String(row[0]));
      if (!existing) continue;
      const unavailable = [...existing.querySelectorAll('.badge')].some((badge) => badge.textContent.trim() === strings.soldOut);
      if (unavailable === row[2]) {
        sessionStorage.removeItem(storageKey);
        throw new Error('Availability changed');
      }
    }
    await loadMissingCards(pageRows);
    if (location.href !== initial.href || document.querySelector('#product-grid > ul') !== grid) return;
    const pageIds = pageRows.map((row) => String(row[0]));
    if (initialIds.join(',') !== pageIds.join(',')) {
      const focused = grid.contains(document.activeElement) ? document.activeElement : null;
      const fragment = document.createDocumentFragment();
      pageRows.forEach((row) => fragment.append(nativeCards.get(String(row[0]))));
      grid.replaceChildren(fragment);
      focused?.focus({ preventScroll: true });
    }
    pageRows.forEach((row) => { nativeCards.get(String(row[0])).dataset.crgSnapshotId = row[0]; });
    status.dataset.verified = 'true';
    status.dataset.page = page;
    say('verified', { count: rows.length, page, pages: Math.ceil(rows.length / pageSize) });
  };
  run().catch(fallback);
})();
